/**
 * The part of a `Worker` that {@link watchWorkerLoad} needs, so tests can pass
 * a plain `EventTarget`.
 */
export type WatchableWorker = Pick<
  Worker,
  'addEventListener' | 'removeEventListener'
>;

/** A load failure reported by {@link watchWorkerLoad}. */
export interface WorkerLoadFailure {
  /** Readable reason, suitable for logs. */
  reason: string;
  /**
   * True when the worker only ran out of time. It may still finish loading,
   * unlike the other failures.
   */
  isTimeout: boolean;
}

/** Handle returned by {@link watchWorkerLoad}. */
export interface WorkerLoadWatch {
  /**
   * Stops watching: clears the timeout and detaches the listeners. Call it
   * when the worker reports its own load outcome, or before discarding the
   * worker. Safe to call more than once.
   */
  settle: () => void;
}

/**
 * Watches a newly created worker until its load outcome is known, and reports
 * the failures the worker cannot report itself: a script that fails to start,
 * an uncaught error during load, an unreadable message, or no outcome at all
 * within `timeoutMs`.
 *
 * The timeout window is the half-open interval `[start, start + timeoutMs)`:
 * calling `settle` at any point before `timeoutMs` has fully elapsed prevents
 * the timeout failure. `onFailure` fires at most once per watch.
 *
 * @param params.worker The worker to watch.
 * @param params.timeoutMs How long the worker has to report a load outcome
 *   before it counts as failed.
 * @param params.onFailure Called with the failure when loading fails.
 * @return A handle whose `settle` ends the watch.
 * @throws Never.
 */
export function watchWorkerLoad({
  worker,
  timeoutMs,
  onFailure,
}: {
  worker: WatchableWorker;
  timeoutMs: number;
  onFailure: (failure: WorkerLoadFailure) => void;
}): WorkerLoadWatch {
  let isSettled = false;

  const fail = (reason: string, isTimeout = false) => {
    if (isSettled) {
      return;
    }
    settle();
    onFailure({reason, isTimeout});
  };

  // A script that fails to fetch or parse fires a bare `Event`; an uncaught
  // exception fires an `ErrorEvent` that carries a message.
  const handleError = (event: Event) => {
    const message =
      'message' in event && typeof event.message === 'string'
        ? event.message
        : '';
    fail(
      message
        ? `Python runner crashed while loading: ${message}`
        : 'Python runner script failed to start',
    );
  };
  const handleMessageError = () => {
    fail('Python runner sent a message that could not be read');
  };

  const timer = setTimeout(() => {
    fail(`Python runner did not finish loading within ${timeoutMs} ms`, true);
  }, timeoutMs);
  worker.addEventListener('error', handleError);
  worker.addEventListener('messageerror', handleMessageError);

  function settle() {
    if (isSettled) {
      return;
    }
    isSettled = true;
    clearTimeout(timer);
    worker.removeEventListener('error', handleError);
    worker.removeEventListener('messageerror', handleMessageError);
  }

  return {settle};
}
